import { StarFilled, StarOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from '@tanstack/react-router';
import PermitSuperAdmin from '@zeus/core/PermitSuperAdmin';
import { useTopHeader } from '_/contexts/LayoutContext';
import NewOrganizationModal from '_/features/organizations/forms/NewOrganizationModal';
import { useAllOrganizations, useMyOrganizations, useAssignOrgToMe, useUnassignOrgFromMe } from '_/hooks/useOrganizations';
import type { IOrganization } from '_/types/organizations/organization.types';
import { Button, Spin, Switch, Input, Space, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useState, useEffect } from 'react';

interface StarredProps {
  data: IOrganization;
  assignOrgToMe: (org: IOrganization) => void;
  unassignOrgFromMe: (org: IOrganization) => void;
  isLoading: boolean;
}

function Starred({ data, assignOrgToMe, unassignOrgFromMe, isLoading }: StarredProps) {
  if (isLoading) {
    return <Spin size="small" />;
  }
  return data.mine ? (
    <Button type="text" shape="circle" onClick={() => unassignOrgFromMe(data)} size="small" icon={<StarFilled />} />
  ) : (
    <Button type="text" shape="circle" onClick={() => assignOrgToMe(data)} size="small" icon={<StarOutlined />} />
  );
}

interface OrganizationsProps {
  superAdmin: boolean;
}

function Organizations({ superAdmin }: OrganizationsProps) {
  const history = useNavigate();

  // React Query hooks
  const {
    data: allOrganizations = [],
    isLoading: isLoadingAll,
    refetch: refetchAll,
    isFetching: isFetchingAll,
  } = useAllOrganizations(superAdmin);
  const {
    data: myOrganizations = [],
    isLoading: isLoadingMy,
    refetch: refetchMy,
    isFetching: isFetchingMy,
  } = useMyOrganizations();
  const assignMutation = useAssignOrgToMe();
  const unassignMutation = useUnassignOrgFromMe();

  const isLoading = isLoadingAll || isLoadingMy;
  const isFetching = isFetchingAll || isFetchingMy;

  // If user is superadmin and has no orgs show them all the orgs
  const defaultShowMine = superAdmin && !myOrganizations?.length ? false : true;

  const [showMine, setMine] = useState(defaultShowMine);
  const [searchText, setSearchText] = useState('');
  const [loadingOrgId, setLoadingOrgId] = useState<string | null>(null);
  const [isNewOrgModalOpen, setIsNewOrgModalOpen] = useState(false);

  // Get layout contexts
  const { setBreadcrumbs, setActionButtons, setOnRefresh, setRefreshLoading } = useTopHeader();

  // Set up layout
  useEffect(() => {
    setBreadcrumbs([{ title: 'Organizations' }]);

    if (superAdmin) {
      setActionButtons([
        {
          onClick: () => setIsNewOrgModalOpen(true),
          icon: <PlusCircleOutlined />,
          label: 'Add Organization',
        },
      ]);
    }

    setOnRefresh(() => {
      refetchAll();
      refetchMy();
    });

    return () => {
      setBreadcrumbs([]);
      setActionButtons([]);
      setOnRefresh(undefined);
    };
  }, [setBreadcrumbs, setActionButtons, setOnRefresh, superAdmin, history, refetchAll, refetchMy]);

  // Update refresh loading state
  useEffect(() => {
    setRefreshLoading(isFetching);
  }, [isFetching, setRefreshLoading]);

  const handleAssignOrgToMe = (org: IOrganization) => {
    setLoadingOrgId(org.id);
    assignMutation.mutate(org, {
      onSettled: () => setLoadingOrgId(null),
    });
  };

  const handleUnassignOrgFromMe = (org: IOrganization) => {
    setLoadingOrgId(org.id);
    unassignMutation.mutate(org, {
      onSettled: () => setLoadingOrgId(null),
    });
  };

  const columns: ColumnsType<IOrganization> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      render: (text, record) => <a onClick={() => void history({ to: `/orgs/${record.slug}/home` })}>{text}</a>,
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      sorter: (a, b) => (a.slug || '').localeCompare(b.slug || ''),
    },
    {
      title: 'Created On',
      dataIndex: 'addedAt',
      key: 'addedAt',
      sorter: (a, b) => dayjs(a.addedAt).unix() - dayjs(b.addedAt).unix(),
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];

  if (superAdmin) {
    columns.push({
      title: '',
      key: 'starred',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Starred
          data={record}
          assignOrgToMe={handleAssignOrgToMe}
          unassignOrgFromMe={handleUnassignOrgFromMe}
          isLoading={loadingOrgId === record.id}
        />
      ),
    });
  }

  // Mark organizations as "mine" in allOrganizations list
  const myOrgIds = new Set(myOrganizations.map((org) => org.id));
  const allOrgsWithMineFlag = allOrganizations.map((org) => ({
    ...org,
    mine: myOrgIds.has(org.id),
  }));

  const baseOrganizations = showMine ? myOrganizations : allOrgsWithMineFlag;
  const organizations = searchText
    ? baseOrganizations.filter(
        (org) =>
          (org.name || '').toLowerCase().includes(searchText.toLowerCase()) ||
          (org.slug || '').toLowerCase().includes(searchText.toLowerCase()),
      )
    : baseOrganizations;

  return (
    <>
      <Space orientation="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Input
            placeholder="Search organizations..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <PermitSuperAdmin>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Switch checked={showMine} onChange={(checked) => setMine(checked)} />
              <span>Mine</span>
            </div>
          </PermitSuperAdmin>
        </div>
        <Table
          dataSource={organizations}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: true }}
          pagination={{
            pageSize: 100,
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} organizations`,
          }}
        />
      </Space>

      <NewOrganizationModal open={isNewOrgModalOpen} onClose={() => setIsNewOrgModalOpen(false)} />
    </>
  );
}

export default Organizations;
